/**
 * The reader's place in a book, on the client.
 *
 * There is exactly one thing stored: a **sid** (plus the `editionId` it is meaningful in — LAW 1).
 * Not a page number, not a scroll offset, not a percentage, not a byte offset into an audio file.
 * Everything the product promises falls out of that and nothing else:
 *
 *   · change the font size mid-page and land on the same sentence  — the pin is a sid
 *   · rotate and land on the same sentence                          — the pin is a sid
 *   · close the tab, reopen on a MacBook, resume                    — the sid is device-independent
 *   · a phone and a laptop in one room agree about the page         — they share a sid, not a page
 *
 * ⚠️ LAW 2. A sid is only meaningful inside its edition. Resolving one against a different edition
 * is not an error anyone will ever see: it silently addresses *a different sentence that happens to
 * exist*, and the reader lights the wrong line. Every position that comes back from the server or
 * out of localStorage goes through `resolveForEdition()` before it is allowed near the DOM, and a
 * mismatch produces a visible message, never a guess.
 */

import type { PositionKind, StoredPosition } from "../../../shared/position.ts";
import { newerPosition } from "../../../shared/position.ts";
import { parseSid } from "../../../shared/address.ts";
import type { Block } from "../../../shared/doc.ts";

/* ══════════════════════════════════════════════════════════════════════════
 * Device identity
 * ══════════════════════════════════════════════════════════════════════════ */

const DEVICE_KEY = "ab.device";

/**
 * A stable per-browser id, stored beside the positions it labels.
 *
 * It exists so "you were reading this on your phone" is answerable, and so a device that syncs a
 * position can recognise its own echo. It is NOT an identity: the actor id is the `abk` cookie's
 * business, and this value must never be sent anywhere it could act as one.
 *
 * A standalone PWA has its own storage and its own cookie jar (§5.8), so installing the app to the
 * home screen produces a *second* device id for the same phone. That is correct — it really is a
 * separate storage context — and it is the reason positions are reconciled on the server rather
 * than assumed unique per human.
 */
export function deviceId(): string {
  try {
    const found = localStorage.getItem(DEVICE_KEY);
    if (found) return found;
    const made = "dev_" + randomId(12);
    localStorage.setItem(DEVICE_KEY, made);
    return made;
  } catch {
    // Private browsing, or storage disabled. A per-session id is still better than none: it keeps
    // this tab's writes distinguishable for the length of the tab.
    return "dev_ephemeral";
  }
}

function randomId(bytes: number): string {
  const buf = new Uint8Array(bytes);
  crypto.getRandomValues(buf);
  return [...buf].map((b) => b.toString(16).padStart(2, "0")).join("");
}

/* ══════════════════════════════════════════════════════════════════════════
 * The ordinal
 * ══════════════════════════════════════════════════════════════════════════ */

/**
 * `BookPos.ord` is the sentence's **dense 0-based global ordinal** within its edition
 * (`sentences.globalOrd`), and it is what `comparePos` sorts on. The reader does not always have
 * it: `GET /books/:id/chapters/:ord` returns blocks with `Span[]`, and a `Span.ord` is the
 * sentence's ordinal *within its block* — a different number entirely.
 *
 * Three tiers, best first. Which one was used is reported on the index, because the difference is
 * invisible until it is not:
 *
 *  1. **exact** — the payload carries a per-sentence `globalOrd`, or a chapter-level base ordinal
 *     we can add an in-chapter index to. This is the server's own number and is the only one that
 *     can be compared against an ordinal the *server* computed.
 *  2. **derived** — `chapterOrd·10⁷ + blockOrd·10³ + sentOrd`, from the address alone.
 *
 * A derived ordinal induces **exactly the same total order** as the dense one for any two sids in
 * one edition, which is all `comparePos` and `newerPosition` need. What it does not preserve is
 * *magnitude*, so `posDeltaOrd` ("3 pages ahead", §2.1) is meaningless against it, and comparing a
 * derived ordinal with a server-computed dense one is meaningless in both directions. Neither is
 * used in P1 — but a server that starts writing `positions.ord` itself (the co-read tick, P5) and
 * a client that is still deriving would disagree with no error anywhere, which is why this comment
 * is long and why `OrdIndex.exact` is on the object rather than in someone's head.
 */
export function derivedOrd(sid: string): number | undefined {
  const a = parseSid(sid);
  if (!a) return undefined;
  // Radix chosen from the padding widths in shared/address.ts: BLOCK_PAD = 4 caps a chapter at
  // 10,000 blocks, so 10³ for the sentence field is the only assumption, and a block with 1,000
  // sentences would be a 30,000-word paragraph.
  return a.chapterOrd * 10_000_000 + a.blockOrd * 1_000 + a.sentOrd;
}

export interface OrdIndex {
  ordOf(sid: string): number | undefined;
  /** False when ordinals are derived from the address rather than supplied by the server. */
  readonly exact: boolean;
}

/** A `Span` that the chapter payload has decorated with the server's dense ordinal. */
type MaybeOrderedSpan = { id: string; globalOrd?: number };

/**
 * Build the sid → ord index for one loaded chapter.
 *
 * `chapterBaseOrd` is the `globalOrd` of the chapter's first sentence, if the payload supplies it
 * (one integer per chapter, and it upgrades every sid in that chapter from derived to exact).
 */
export function buildOrdIndex(blocks: readonly Block[], chapterBaseOrd?: number | null): OrdIndex {
  const map = new Map<string, number>();
  let exact = true;
  let i = 0;

  for (const block of blocks) {
    for (const span of block.sentences as readonly MaybeOrderedSpan[]) {
      if (typeof span.globalOrd === "number" && Number.isFinite(span.globalOrd)) {
        map.set(span.id, span.globalOrd);
      } else if (typeof chapterBaseOrd === "number" && Number.isFinite(chapterBaseOrd)) {
        map.set(span.id, chapterBaseOrd + i);
      } else {
        const d = derivedOrd(span.id);
        if (d !== undefined) map.set(span.id, d);
        exact = false;
      }
      i++;
    }
  }

  return {
    ordOf: (sid) => map.get(sid) ?? derivedOrd(sid),
    exact,
  };
}

/* ══════════════════════════════════════════════════════════════════════════
 * Building a position
 * ══════════════════════════════════════════════════════════════════════════ */

export interface MakePositionArgs {
  sid: string;
  editionId: string;
  ord: number;
  kind?: PositionKind;
  /** Fraction through the sentence. 0 for a reading position — see the note on `ms` below. */
  frac?: number;
  /** Milliseconds on the book's linear narration axis. */
  ms?: number;
}

export function makePosition(a: MakePositionArgs): StoredPosition {
  const addr = parseSid(a.sid);
  if (!addr) throw new TypeError(`not a sentence id: ${a.sid}`);

  return {
    sid: a.sid,
    ord: a.ord,
    // LAW 3: **`ms` is advisory. It is never used to reconstruct a position.** A reading position
    // in P1 has no narration behind it, so there is no honest millisecond to report and 0 is the
    // identity on that axis rather than a claim about it. Nothing may read this back to seek: a
    // client resolves an in-page offset from *its own* cue list, by sid + frac. If you find code
    // anywhere that does arithmetic on a stored `ms`, that is the bug LAW 3 exists to name.
    ms: a.ms ?? 0,
    frac: clamp01(a.frac ?? 0),
    editionId: a.editionId,
    chapterOrd: addr.chapterOrd,
    kind: a.kind ?? "read",
    deviceId: deviceId(),
    clientUpdatedAt: Date.now(),
  };
}

function clamp01(n: number): number {
  return !Number.isFinite(n) ? 0 : n < 0 ? 0 : n > 1 ? 1 : n;
}

/* ══════════════════════════════════════════════════════════════════════════
 * LAW 2 — resolving a stored position against the edition on screen
 * ══════════════════════════════════════════════════════════════════════════ */

export type ResolvedPosition =
  | { state: "none" }
  /** Same edition. The sid means what it says. */
  | { state: "ok"; pos: StoredPosition }
  /**
   * A different edition. The book was re-imported since this position was written, so the sid
   * addresses a sentence in an edition that is no longer on screen. **Never resolve it.** The
   * remap ladder (`doc_remaps`, §3.6) is the server's job and lands in a later phase; until then
   * the reader says so — §2.1's own words: "we couldn't find your place after the book was
   * updated — jump to chapter 9?" — and starts at the body start.
   */
  | { state: "stale"; pos: StoredPosition; wantedEditionId: string };

export function resolveForEdition(
  pos: StoredPosition | null | undefined,
  editionId: string | null | undefined,
): ResolvedPosition {
  if (!pos || !pos.sid) return { state: "none" };
  if (!editionId) return { state: "none" };
  if (pos.editionId !== editionId) return { state: "stale", pos, wantedEditionId: editionId };
  if (!parseSid(pos.sid)) return { state: "none" };
  return { state: "ok", pos };
}

/* ══════════════════════════════════════════════════════════════════════════
 * Local mirror
 * ══════════════════════════════════════════════════════════════════════════ */

const localKey = (bookId: string, kind: PositionKind) => `ab.pos.${bookId}.${kind}`;

/**
 * The local mirror is not a cache of the server's answer; it is the answer that is available
 * *first*. A cold open on a phone with one bar renders the book from cache and needs a page to
 * land on before the position round-trip completes, and landing on page 1 and then jumping is
 * worse than landing late.
 */
export function loadLocal(bookId: string, kind: PositionKind = "read"): StoredPosition | null {
  try {
    const raw = localStorage.getItem(localKey(bookId, kind));
    if (!raw) return null;
    const p = JSON.parse(raw) as StoredPosition;
    // A stored blob whose shape does not match is discarded rather than repaired. It is one
    // reading position; guessing at it is how a corrupt entry becomes a permanent wrong landing.
    if (typeof p?.sid !== "string" || typeof p?.editionId !== "string") return null;
    if (typeof p.clientUpdatedAt !== "number") return null;
    return p;
  } catch {
    return null;
  }
}

export function saveLocal(bookId: string, pos: StoredPosition): void {
  try {
    localStorage.setItem(localKey(bookId, pos.kind), JSON.stringify(pos));
  } catch {
    /* Quota, or private browsing. The server copy is authoritative; this one is an optimisation. */
  }
}

/**
 * Merge the local mirror with the server's copy. **Last write wins, not furthest wins** —
 * `newerPosition` in shared/position.ts, and the distinction is deliberate: a reader who goes back
 * to re-read chapter 2 has not lost their place at chapter 9, they have moved it, and a
 * furthest-wins merge would drag them forward every time they opened the book.
 */
export function mergePositions(
  a: StoredPosition | null,
  b: StoredPosition | null,
): StoredPosition | null {
  if (!a) return b;
  if (!b) return a;
  return newerPosition(a, b);
}

/**
 * Of a `read` and a `listen` position, the one to resume from: **the newer**, by the same
 * last-write-wins rule — except that a copy which cannot be placed in the edition on screen never
 * hides one that can (LAW 2's "resolve first, merge second", one level down).
 *
 * The two exist because they are written by different things. Turning a page writes `read`; the
 * audio engine writes `listen` at page boundaries and pauses, which are the only moments a locked
 * phone can write anything. A book listened to in the car for an hour has a `read` position an hour
 * stale, and opening it there is the bug this exists to fix.
 */
export function newestPlace(
  a: StoredPosition | null | undefined,
  b: StoredPosition | null | undefined,
  editionId: string | null | undefined,
): StoredPosition | null {
  const x = a ?? null;
  const y = b ?? null;
  if (x === null || y === null) return x ?? y;
  if (editionId) {
    const xOk = x.editionId === editionId;
    const yOk = y.editionId === editionId;
    if (xOk !== yOk) return xOk ? x : y;
  }
  return mergePositions(x, y);
}

/* ══════════════════════════════════════════════════════════════════════════
 * Where the book opens
 *
 * Lives here rather than in `Reader.tsx` for one reason: **it is LAW 2's decision point and it must
 * be testable.** `Reader.tsx` imports four stylesheets, so nothing under `node --test` can import it,
 * and this function shipped with an argument-order bug that silently deleted the entire remap ladder
 * (see `chooseOpening` below) with no test able to reach it. This file imports nothing but shared
 * types.
 * ══════════════════════════════════════════════════════════════════════════ */

/** The subset of `Chapter` this decision needs. Structural, so a real `Chapter` satisfies it. */
export interface OpeningChapter {
  ord: number;
  role: string;
}

export interface OpeningInput {
  /** `#s=<sid>` — someone shared this passage (§5.6). */
  hashSid: string | null;
  /** An explicit request from the page that mounted the reader. */
  propSid: string | null;
  /** `GET /books/:id/start`: `sid` is bodyStart-or-better, `position` is the resolved saved row. */
  server: { sid: string | null; position: StoredPosition | null };
  /** The localStorage mirror. */
  local: StoredPosition | null;
  /** `books.currentEditionId` — the edition actually on screen. */
  editionId: string | null;
  readingOrder: readonly OpeningChapter[];
}

export interface Opening {
  chapterOrd: number;
  sid: string | null;
  notice: string | null;
}

export function validSid(sid: string | null | undefined): string | null {
  return sid && parseSid(sid) ? sid : null;
}

function chapterOrdOf(sid: string): number | null {
  return parseSid(sid)?.chapterOrd ?? null;
}

/**
 * Precedence, and the LAW 2 check.
 *
 *  1. `#s=<sid>` — an explicit request always wins.
 *  2. an explicit prop, for a page that already knows where it wants to open.
 *  3. the newer of the server's saved position and the local mirror — **last write wins, not
 *     furthest wins**, because a reader who went back to re-read chapter 2 has *moved* their place.
 *  4. the server's `bodyStart` (§4.8).
 *  5. the first body chapter in reading order.
 *
 * ⚠️ **RESOLVE FIRST, MERGE SECOND. The other order deletes the remap ladder.**
 *
 * `mergePositions` is last-write-wins on `clientUpdatedAt`, and `newerPosition` returns its FIRST
 * argument on a tie. `PositionWriter.update` mirrors into localStorage with the *identical*
 * `clientUpdatedAt` it PUTs, so the local copy always ties or wins.
 *
 * Now take a re-imported book, which is the case the ladder exists for. The server has already done
 * the work: `resolveAgainstCurrent` walked `doc_remaps`, found the sentence in the new edition, and
 * answered `state:"remapped"` carrying the CURRENT `editionId`. Merging that against a localStorage
 * mirror still stamped with the OLD edition handed the stale one back on the tie,
 * `resolveForEdition` called it `"stale"`, and the reader showed *"We couldn't find your place —
 * this book was updated since you last read it"* and dumped the reader at `bodyStart`.
 *
 * The place had been found. Nothing errored, and the notice actively misinformed. Every re-import
 * lost every reader's place on every device that had already read the book.
 *
 * So each candidate is resolved on its own, only what resolves is merged, and the notice is raised
 * only when there was a saved position and NEITHER copy could be placed.
 */
export function chooseOpening(i: OpeningInput): Opening {
  const first = i.readingOrder.find((c) => c.role === "body") ?? i.readingOrder[0];
  const fallbackOrd = first?.ord ?? 0;

  const explicit = validSid(i.hashSid) ?? validSid(i.propSid);
  if (explicit) {
    return { chapterOrd: chapterOrdOf(explicit) ?? fallbackOrd, sid: explicit, notice: null };
  }

  const localR = resolveForEdition(i.local, i.editionId);
  const serverR = resolveForEdition(i.server.position, i.editionId);
  const saved = mergePositions(
    localR.state === "ok" ? localR.pos : null,
    serverR.state === "ok" ? serverR.pos : null,
  );

  if (saved) {
    return { chapterOrd: chapterOrdOf(saved.sid) ?? fallbackOrd, sid: saved.sid, notice: null };
  }

  const notice =
    localR.state === "stale" || serverR.state === "stale"
      ? "We couldn't find your place — this book was updated since you last read it. Starting at the beginning."
      : null;

  const bodyStart = validSid(i.server.sid);
  if (bodyStart) {
    return { chapterOrd: chapterOrdOf(bodyStart) ?? fallbackOrd, sid: bodyStart, notice };
  }
  return { chapterOrd: fallbackOrd, sid: null, notice };
}

/* ══════════════════════════════════════════════════════════════════════════
 * The wire
 * ══════════════════════════════════════════════════════════════════════════ */

const base = (bookId: string) => `/api/v1/books/${encodeURIComponent(bookId)}/position`;

/**
 * `GET /api/v1/books/:id/position`.
 *
 * The `positions` table is keyed `(actorKey, bookId, kind)`, so the natural response is up to
 * three rows. The shape the route actually returns is the server agent's to choose; this accepts
 * the three plausible ones (a keyed object, an array, a bare position) and normalises. That is
 * tolerance about the *envelope* only — the contents are used exactly as typed, and a row whose
 * `sid` or `editionId` is missing is dropped rather than defaulted (LAW 9: absent ≠ zero).
 */
export async function fetchPositions(
  bookId: string,
  signal?: AbortSignal,
): Promise<Partial<Record<PositionKind, StoredPosition>>> {
  const res = await fetch(base(bookId), { credentials: "same-origin", signal });
  if (!res.ok) {
    if (res.status === 404) return {};
    throw new Error(`position fetch failed: ${res.status}`);
  }
  const body: unknown = await res.json();
  const out: Partial<Record<PositionKind, StoredPosition>> = {};

  const take = (p: unknown) => {
    if (!p || typeof p !== "object") return;
    const c = p as Partial<StoredPosition>;
    if (typeof c.sid !== "string" || typeof c.editionId !== "string") return;
    if (typeof c.clientUpdatedAt !== "number") return;
    const kind: PositionKind = c.kind === "listen" || c.kind === "furthest" ? c.kind : "read";
    out[kind] = { ...(c as StoredPosition), kind };
  };

  if (Array.isArray(body)) body.forEach(take);
  else if (body && typeof body === "object") {
    const o = body as Record<string, unknown>;
    if ("sid" in o) take(o);
    else {
      for (const k of ["read", "listen", "furthest"] as const) take(o[k]);
      if ("positions" in o) {
        const nested = o["positions"];
        if (Array.isArray(nested)) nested.forEach(take);
        else if (nested && typeof nested === "object") {
          for (const k of ["read", "listen", "furthest"] as const) {
            take((nested as Record<string, unknown>)[k]);
          }
        }
      }
    }
  }
  return out;
}

/**
 * Debounced writer.
 *
 * Two channels, and they are not interchangeable:
 *
 *  · `PUT /position` — the normal write, debounced, `credentials: same-origin`.
 *  · `POST /position/beacon` — **the only write that fires when an iPhone locks.** A `PUT` issued
 *    from a `visibilitychange` handler is cancelled by the browser as the document is frozen; a
 *    beacon is queued by the network stack and survives. §10 marks the body `text/plain` for the
 *    same mechanical reason `sendBeacon` exists: a Blob with a JSON content type makes it a
 *    preflighted CORS request in some engines, and a preflight cannot complete after freeze.
 *
 * Turning a page is the *only* thing that moves this in P1, and it is cheap — but a reader flicking
 * through a chapter fires one per turn, so the debounce is what keeps that from being forty PUTs.
 */
export class PositionWriter {
  private readonly bookId: string;
  private readonly delayMs: number;
  private timer: ReturnType<typeof setTimeout> | null = null;
  private pending: StoredPosition | null = null;
  private sentAt = 0;
  private disposed = false;
  private readonly onHide: () => void;

  constructor(bookId: string, delayMs = 1500) {
    this.bookId = bookId;
    this.delayMs = delayMs;

    this.onHide = () => {
      if (document.visibilityState === "hidden") this.beacon();
    };
    document.addEventListener("visibilitychange", this.onHide);
    // `pagehide` and not `unload`: iOS Safari does not reliably fire `unload`, and registering an
    // `unload` handler disqualifies the page from the back/forward cache — which would turn every
    // return-to-the-book into a full reload and a fresh pagination.
    addEventListener("pagehide", this.onHide);
  }

  /** Record a new position. Mirrors locally immediately; the network write is debounced. */
  update(pos: StoredPosition): void {
    if (this.disposed) return;
    this.pending = pos;
    saveLocal(this.bookId, pos);
    if (this.timer) return;
    this.timer = setTimeout(() => {
      this.timer = null;
      void this.flush();
    }, this.delayMs);
  }

  /** Send now. Safe to call with nothing pending. */
  async flush(): Promise<void> {
    const pos = this.pending;
    if (!pos || pos.clientUpdatedAt === this.sentAt) return;
    this.sentAt = pos.clientUpdatedAt;
    /**
     * Which kind of failure it was, and it is load-bearing rather than diagnostic.
     *
     * A `fetch` that **throws** is the transport failing — no route, a captive portal, a dead AP —
     * and that is the one signal the app has that the network is gone when `navigator.onLine` is
     * still `true`. A 5xx is the opposite: the network worked perfectly and cloud-2 had a moment.
     * Reporting the second as "offline" would make a downloaded book stop streaming because the
     * server hiccuped once.
     */
    let cause: "transport" | "server" = "transport";
    try {
      const res = await fetch(base(this.bookId), {
        method: "PUT",
        headers: { "content-type": "application/json" },
        credentials: "same-origin",
        body: JSON.stringify(wire(pos)),
      });
      // A 5xx is the server having a moment; a 4xx is the server refusing these exact bytes and it
      // will refuse them again. Only the first is worth queueing — see `outbox.ts`, which makes the
      // same three-way distinction on its own retries.
      if (res.status >= 500) {
        cause = "server";
        throw new Error(`position PUT answered ${res.status}`);
      }
    } catch {
      // Offline, or the tab is going away. The local mirror still holds it and the next successful
      // write carries the newer `clientUpdatedAt`, so nothing is lost that the merge cannot fix.
      this.sentAt = 0;
      /**
       * P6, §5.8. Queue it so it survives the tab as well as the page.
       *
       * ⚠️ The queue coalesces by `(bookId, kind)` and keeps the **greater `clientUpdatedAt`** — last
       * write wins, never furthest wins. A reader who went back to re-read chapter 2 in a tunnel has
       * *moved* their place; a furthest-wins queue would drag them to chapter 9 the moment the train
       * came out of it, with nothing in any log. `wire(pos)` is passed through rather than rebuilt
       * inside the outbox, so the queued body and the live body cannot diverge — including LAW 9's
       * `ord`/`ms`/`frac`, which the server 400s on if any is absent.
       *
       * ⚠️ **Imported dynamically, and that is not laziness.** This file's header records why it
       * exists at all: `chooseOpening` is LAW 2's decision point and it must be testable, so
       * `test/opening.test.ts` imports this module under `node --test` — which it can only do while
       * this file imports nothing but shared types. A static import of the outbox would drag in
       * IndexedDB, React and the audio engine's page window at load time, and the test that guards
       * the remap ladder would be the thing that broke. `client/src/main.tsx` imports the outbox
       * statically, so at runtime this resolves to a module that is already in the entry bundle —
       * there is no extra chunk to fetch, which matters because the moment we need it is the moment
       * there is no network.
       */
      void import("./outbox.ts").then((m) =>
        m.enqueuePosition(
          this.bookId,
          pos.kind,
          pos.clientUpdatedAt,
          wire(pos),
          `Where you were reading · ${this.bookId}`,
          cause,
        ),
      );
    }
  }

  /** Fire-and-forget, survives an iOS lock. Never awaited, never throws. */
  beacon(): void {
    const pos = this.pending;
    if (!pos || pos.clientUpdatedAt === this.sentAt) return;
    this.sentAt = pos.clientUpdatedAt;
    const body = JSON.stringify(wire(pos));
    try {
      const ok = navigator.sendBeacon?.(
        `${base(this.bookId)}/beacon`,
        new Blob([body], { type: "text/plain;charset=UTF-8" }),
      );
      if (!ok) void this.flush();
    } catch {
      void this.flush();
    }
  }

  dispose(): void {
    this.disposed = true;
    if (this.timer) clearTimeout(this.timer);
    this.timer = null;
    document.removeEventListener("visibilitychange", this.onHide);
    removeEventListener("pagehide", this.onHide);
  }
}

/** Exactly the fields §10 names for `PUT /api/v1/books/:id/position`, and no others. */
function wire(p: StoredPosition): Record<string, unknown> {
  return {
    kind: p.kind,
    sid: p.sid,
    ord: p.ord,
    ms: p.ms,
    frac: p.frac,
    editionId: p.editionId,
    chapterOrd: p.chapterOrd,
    deviceId: p.deviceId ?? null,
    clientUpdatedAt: p.clientUpdatedAt,
  };
}
